import { useState } from 'react'
import { ENV } from '@shared/config/env'
import { classifyAuthError, parse429 } from '@shared/api/httpErrors'
import { formatResendWaitMessage } from '@features/web-auth/model/useResendTimer'
import { useInvalidateProfile } from '@entities/user'
import { linkEmailSendCode, linkEmailVerifyCode } from '../api/linkEmailApi'

export type LinkEmailStep = 'email' | 'will-merge-confirm' | 'code' | 'done' | 'email-taken'

interface UseLinkEmailFlowParams {
  /** Вызывается для закрытия модалки снаружи */
  onClose: () => void
}

interface UseLinkEmailFlowReturn {
  step: LinkEmailStep
  email: string
  nextResendAt: string | null
  error: string | null
  attemptsLeft: number | null
  isLocked: boolean
  shakeSignal: number
  submitting: boolean
  debugCode: string | null

  submitEmail: (email: string) => Promise<void>
  confirmMerge: () => void
  submitCode: (code: string) => Promise<void>
  resendCode: () => Promise<void>
  backToEmail: () => void
}

/**
 * Бизнес-логика модалки привязки email из TG-версии.
 * Управляет шагами, состоянием ошибок и вызовами API.
 * @param params.onClose - Закрытие модалки (вызывается при success-кейсах "уже привязан")
 * @returns Состояние и обработчики флоу
 */
export const useLinkEmailFlow = ({ onClose }: UseLinkEmailFlowParams): UseLinkEmailFlowReturn => {
  const invalidateProfile = useInvalidateProfile()
  const [step, setStep] = useState<LinkEmailStep>('email')
  const [email, setEmail] = useState('')
  const [nextResendAt, setNextResendAt] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [attemptsLeft, setAttemptsLeft] = useState<number | null>(null)
  const [isLocked, setIsLocked] = useState(false)
  const [shakeSignal, setShakeSignal] = useState(0)
  const [submitting, setSubmitting] = useState(false)
  const [debugCode, setDebugCode] = useState<string | null>(null)

  const submitEmail = async (value: string) => {
    setSubmitting(true); setError(null)
    try {
      const res = await linkEmailSendCode(value)
      setEmail(value)
      setNextResendAt(res.next_resend)
      if (ENV.IS_DEV && res.code) setDebugCode(res.code)
      setStep(res.will_merge ? 'will-merge-confirm' : 'code')
    } catch (err) {
      const cooldown = parse429(err)
      if (cooldown) {
        setEmail(value)
        setNextResendAt(cooldown.next_resend)
        setStep('code')
        setError(formatResendWaitMessage(cooldown.next_resend) ?? cooldown.message)
        return
      }
      const kind = classifyAuthError(err)
      if (kind === 'email_taken_by_other_tg') setStep('email-taken')
      else if (kind === 'user_already_has_email') { await invalidateProfile(); onClose() }
      else setError('Не удалось отправить код')
    } finally {
      setSubmitting(false)
    }
  }

  const confirmMerge = () => setStep('code')

  const submitCode = async (code: string) => {
    setSubmitting(true); setError(null)
    try {
      await linkEmailVerifyCode(email, code)
      await invalidateProfile()
      setStep('done')
    } catch (err) {
      const kind = classifyAuthError(err)
      switch (kind) {
        case 'code_invalid':
          setShakeSignal((x) => x + 1)
          setError('Неверный код.')
          break
        case 'code_expired':
        case 'code_not_found':
          setError('Срок действия кода истёк.')
          setIsLocked(true)
          break
        case 'max_attempts':
        case 'rate_limit':
          setIsLocked(true)
          setError('Превышено количество попыток. Запросите новый код.')
          break
        case 'user_already_has_email':
          await invalidateProfile(); onClose()
          return
        case 'email_taken_by_other_tg':
          setStep('email-taken')
          return
        default:
          setError('Не удалось проверить код')
      }
      setAttemptsLeft(null)
    } finally {
      setSubmitting(false)
    }
  }

  const resendCode = async () => {
    // Для связки email resend = новый send-code.
    await submitEmail(email)
  }

  const backToEmail = () => {
    setStep('email')
    setError(null)
    setIsLocked(false)
    setAttemptsLeft(null)
  }

  return {
    step, email, nextResendAt, error, attemptsLeft, isLocked, shakeSignal, submitting, debugCode,
    submitEmail, confirmMerge, submitCode, resendCode, backToEmail
  }
}
